import { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { confirmSwal } from "../utils/confirm";
import { toast } from "react-toastify";
import { auth } from "../firebase";

export default function AdminEmailLists() {
    const [groups, setGroups] = useState([]);
    const [directory, setDirectory] = useState([]);
    const [committees, setCommittees] = useState([]);
    const [boardRoles, setBoardRoles] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [newGroupData, setNewGroupData] = useState({
        group_name: "",
        description: "",
        directory_members: [],
        external_members: [],
        is_publicly_joinable: false,
        reply_to_email: "",
    });
    const [externalEmail, setExternalEmail] = useState("");
    const [externalName, setExternalName] = useState("");
    const [showMembersMap, setShowMembersMap] = useState({});
    const [selectedStatuses, setSelectedStatuses] = useState([]);
    const [selectedCommittees, setSelectedCommittees] = useState([]);
    const [selectedBoardRoles, setSelectedBoardRoles] = useState([]);
    
    // Edit mode state
    const [editingGroupId, setEditingGroupId] = useState(null);
    const [editGroupData, setEditGroupData] = useState({
        group_name: "",
        description: "",
        directory_members: [],
        external_members: [],
        is_publicly_joinable: false,
        reply_to_email: "",
    });
    const [editExternalEmail, setEditExternalEmail] = useState("");
    const [editExternalName, setEditExternalName] = useState("");
    const [editSelectedStatuses, setEditSelectedStatuses] = useState([]);
    const [editSelectedCommittees, setEditSelectedCommittees] = useState([]);
    const [editSelectedBoardRoles, setEditSelectedBoardRoles] = useState([]);

    useEffect(() => {
        fetchGroups();
        fetchDirectory();
        fetchCommittees();
        fetchBoardRoles();
    }, []);

    const fetchGroups = async () => {
        const token = await auth.currentUser.getIdToken();
        const res = await axios.get("/api/emails", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        setGroups(res.data);
        const map = {};
        res.data.forEach(g => map[g._id] = false);
        setShowMembersMap(map);
    };

    const fetchDirectory = async () => {
        const token = await auth.currentUser.getIdToken();
        const res = await axios.get("/api/directory", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        setDirectory(res.data);
    };

    const fetchCommittees = async () => {
        const token = await auth.currentUser.getIdToken();
        const res = await axios.get("/api/committees", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        setCommittees(res.data);
    };

    const fetchBoardRoles = async () => {
        const token = await auth.currentUser.getIdToken();
        const res = await axios.get("/api/board", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        setBoardRoles(res.data);
    };

    const statusOptions = Array.from(
        new Set(directory.map(u => u.status?.toLowerCase().trim()).filter(Boolean))
    ).map(status => ({ value: status, label: status.charAt(0).toUpperCase() + status.slice(1) }));

    const committeeOptions = committees.map(committee => ({
        value: committee._id,
        label: committee.name
    }));

    const boardRoleOptions = boardRoles.map(role => ({
        value: role._id,
        label: role.name
    }));

    useEffect(() => {
        if (!selectedStatuses.length) return;
        const selected = selectedStatuses.map(s => s.value);
        const matching = directory.filter(u => selected.includes(u.status?.toLowerCase().trim())).map(u => u._id);
        setNewGroupData(prev => ({ ...prev, directory_members: [...new Set([...prev.directory_members, ...matching])] }));
    }, [selectedStatuses, directory]);

    useEffect(() => {
        if (!selectedCommittees.length) return;
        const selectedCommitteeIds = selectedCommittees.map(c => c.value);
        const matching = [];
        
        selectedCommitteeIds.forEach(committeeId => {
            const committee = committees.find(c => c._id === committeeId);
            if (committee && committee.members) {
                committee.members.forEach(member => {
                    if (member.userId) {
                        matching.push(member.userId);
                    }
                });
            }
        });
        
        setNewGroupData(prev => ({ ...prev, directory_members: [...new Set([...prev.directory_members, ...matching])] }));
    }, [selectedCommittees, committees]);

    useEffect(() => {
        if (!selectedBoardRoles.length) return;
        const selectedRoleIds = selectedBoardRoles.map(r => r.value);
        const matching = [];
        
        selectedRoleIds.forEach(roleId => {
            const role = boardRoles.find(r => r._id === roleId);
            if (role && role.members) {
                role.members.forEach(member => {
                    if (member.userId) {
                        matching.push(member.userId);
                    }
                });
            }
        });
        
        setNewGroupData(prev => ({ ...prev, directory_members: [...new Set([...prev.directory_members, ...matching])] }));
    }, [selectedBoardRoles, boardRoles]);

    // Edit form useEffect hooks
    useEffect(() => {
        if (!editSelectedStatuses.length) return;
        const selected = editSelectedStatuses.map(s => s.value);
        const matching = directory.filter(u => selected.includes(u.status?.toLowerCase().trim())).map(u => u._id);
        setEditGroupData(prev => ({ ...prev, directory_members: [...new Set([...prev.directory_members, ...matching])] }));
    }, [editSelectedStatuses, directory]);

    useEffect(() => {
        if (!editSelectedCommittees.length) return;
        const selectedCommitteeIds = editSelectedCommittees.map(c => c.value);
        const matching = [];
        
        selectedCommitteeIds.forEach(committeeId => {
            const committee = committees.find(c => c._id === committeeId);
            if (committee && committee.members) {
                committee.members.forEach(member => {
                    if (member.userId) {
                        matching.push(member.userId);
                    }
                });
            }
        });
        
        setEditGroupData(prev => ({ ...prev, directory_members: [...new Set([...prev.directory_members, ...matching])] }));
    }, [editSelectedCommittees, committees]);

    useEffect(() => {
        if (!editSelectedBoardRoles.length) return;
        const selectedRoleIds = editSelectedBoardRoles.map(r => r.value);
        const matching = [];
        
        selectedRoleIds.forEach(roleId => {
            const role = boardRoles.find(r => r._id === roleId);
            if (role && role.members) {
                role.members.forEach(member => {
                    if (member.userId) {
                        matching.push(member.userId);
                    }
                });
            }
        });
        
        setEditGroupData(prev => ({ ...prev, directory_members: [...new Set([...prev.directory_members, ...matching])] }));
    }, [editSelectedBoardRoles, boardRoles]);

    const handleAddDirectoryMember = () => {
        setNewGroupData((prev) => ({
            ...prev,
            directory_members: [...prev.directory_members, ""]
        }));
    };

    const handleDirectorySelect = (index, selectedOption) => {
        const updated = [...newGroupData.directory_members];
        updated[index] = selectedOption ? selectedOption.value : "";
        setNewGroupData({ ...newGroupData, directory_members: updated });
    };

    const handleRemoveDirectoryMember = (index) => {
        const updated = [...newGroupData.directory_members];
        updated.splice(index, 1);
        setNewGroupData({ ...newGroupData, directory_members: updated });
    };

    // Helper function to get the last used reply-to email
    const getLastReplyToEmail = () => {
        if (!groups.length) return "";
        
        // Sort groups by creation date (most recent first) and find the first one with a reply-to email
        const sortedGroups = [...groups].sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        const groupWithReplyTo = sortedGroups.find(group => group.reply_to_email);
        
        return groupWithReplyTo ? groupWithReplyTo.reply_to_email : "";
    };

    const handleAddExternal = () => {
        if (!externalEmail) return;
        const emailInput = document.createElement("input");
        emailInput.type = "email";
        emailInput.value = externalEmail;
        if (!emailInput.checkValidity()) {
            toast.warning("Please enter a valid email address.");
            return;
        }
        const updated = [...newGroupData.external_members, { email: externalEmail, name: externalName || "Guest" }];
        setNewGroupData({ ...newGroupData, external_members: updated });
        setExternalEmail("");
        setExternalName("");
    };

    const handleCreate = async () => {
        // Auto-add external email if filled but not added yet
        if (externalEmail) {
            const emailInput = document.createElement("input");
            emailInput.type = "email";
            emailInput.value = externalEmail;
            if (!emailInput.checkValidity()) {
                toast.warning("Please enter a valid email address.");
                return;
            }
            newGroupData.external_members.push({ email: externalEmail, name: externalName || "Guest" });
            setExternalEmail("");
            setExternalName("");
        }

        if (!newGroupData.group_name.trim()) {
            toast.warning("Group label is required.");
            return;
        }

        const payload = {
            group_name: newGroupData.group_name,
            description: newGroupData.description,
            member_ids: newGroupData.directory_members,
            external_emails: newGroupData.external_members,
            is_publicly_joinable: newGroupData.is_publicly_joinable,
            reply_to_email: newGroupData.reply_to_email || null
        };

        const token = await auth.currentUser.getIdToken();
        await axios.post("/api/emails", payload, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        setShowCreate(false);
        setNewGroupData({ group_name: "", description: "", directory_members: [], external_members: [], is_publicly_joinable: false, reply_to_email: "" });
        setExternalEmail("");
        setExternalName("");
        setSelectedStatuses([]);
        setSelectedCommittees([]);
        setSelectedBoardRoles([]);
        fetchGroups();
        toast.success("Email list created!");
    };

    const toggleShowMembers = (groupId) => setShowMembersMap(prev => ({ ...prev, [groupId]: !prev[groupId] }));

    const handleArchive = async (id) => {
        const ok = await confirmSwal({
            title: "Delete email list?",
            text: "This cannot be undone.",
            icon: "warning",
            confirmText: "Delete",
            confirmColor: "#d33"
        });
        if (!ok) return;
        const token = await auth.currentUser.getIdToken();
        await axios.delete(`/api/emails/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        fetchGroups();
        toast.info("Email list deleted.");
    };

    const handleAddMemberToGroup = async (groupId, userId) => {
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.post(`/api/emails/${groupId}/add_member`, { user_id: userId }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchGroups();
            toast.success("Added member to group.");
        } catch (err) {
            toast.error("Failed to add member to group.");
        }
    };

    const handleRemoveMemberFromGroup = async (groupId, email) => {
        try {
            const token = await auth.currentUser.getIdToken();
            await axios.post(`/api/emails/${groupId}/remove_member`, { email }, {
                headers: {
                    Authorization: `Bearer ${token}`,
                },
            });
            fetchGroups();
            toast.info("Removed member from group");
        } catch (err) {
            toast.error("Failed to remove member from group.");
        }
    };

    const handleCancel = () => {
        setShowCreate(false);
        setNewGroupData({ group_name: "", description: "", directory_members: [], external_members: [], is_publicly_joinable: false, reply_to_email: "" });
        setExternalEmail("");
        setExternalName("");
        setSelectedStatuses([]);
        setSelectedCommittees([]);
        setSelectedBoardRoles([]);
    };

    // Edit functions
    const handleEditGroup = (group) => {
        setEditingGroupId(group._id);
        setEditGroupData({
            group_name: group.group_name,
            description: group.description || "",
            directory_members: group.members.filter(m => m.user_id).map(m => m.user_id),
            external_members: group.members.filter(m => !m.user_id).map(m => ({ email: m.email, name: m.name })),
            is_publicly_joinable: group.is_publicly_joinable || false,
            reply_to_email: group.reply_to_email || "",
        });
        setEditExternalEmail("");
        setEditExternalName("");
        setEditSelectedStatuses([]);
        setEditSelectedCommittees([]);
        setEditSelectedBoardRoles([]);
    };

    const handleEditCancel = () => {
        setEditingGroupId(null);
        setEditGroupData({ group_name: "", description: "", directory_members: [], external_members: [], is_publicly_joinable: false, reply_to_email: "" });
        setEditExternalEmail("");
        setEditExternalName("");
        setEditSelectedStatuses([]);
        setEditSelectedCommittees([]);
        setEditSelectedBoardRoles([]);
    };

    const handleEditSave = async () => {
        if (!editGroupData.group_name.trim()) {
            toast.warning("Group label is required.");
            return;
        }

        // Show loading toast
        toast.info("Changes are being saved...");

        try {
            const token = await auth.currentUser.getIdToken();
            
            // Update group basic info
            await axios.patch(`/api/emails/${editingGroupId}`, {
                group_name: editGroupData.group_name,
                description: editGroupData.description,
                is_publicly_joinable: editGroupData.is_publicly_joinable,
                reply_to_email: editGroupData.reply_to_email || null
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });

            // Get current group to compare members
            const currentGroup = groups.find(g => g._id === editingGroupId);
            const currentMemberEmails = new Set(currentGroup.members.map(m => m.email));
            
            // Calculate new members (internal + external)
            const newInternalMembers = editGroupData.directory_members.map(userId => {
                const contact = directory.find(d => d._id === userId);
                return contact?.email_address;
            }).filter(Boolean);
            
            const newExternalEmails = editGroupData.external_members.map(m => m.email);
            const allNewEmails = new Set([...newInternalMembers, ...newExternalEmails]);

            // Remove members that are no longer in the list
            for (const member of currentGroup.members) {
                if (!allNewEmails.has(member.email)) {
                    await axios.post(`/api/emails/${editingGroupId}/remove_member`, { email: member.email }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                }
            }

            // Add new internal members
            for (const userId of editGroupData.directory_members) {
                const contact = directory.find(d => d._id === userId);
                if (contact && !currentMemberEmails.has(contact.email_address)) {
                    await axios.post(`/api/emails/${editingGroupId}/add_member`, { user_id: userId }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                }
            }

            // Add new external members
            for (const extMember of editGroupData.external_members) {
                if (!currentMemberEmails.has(extMember.email)) {
                    await axios.post(`/api/emails/${editingGroupId}/add_external_member`, {
                        email: extMember.email,
                        name: extMember.name || "Guest"
                    }, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                }
            }
            
            handleEditCancel();
            fetchGroups();
            toast.success("Group updated successfully!");
        } catch (error) {
            console.error("Error updating group:", error);
            toast.error("Failed to update group");
        }
    };

    const handleEditAddExternal = () => {
        if (!editExternalEmail) return;
        const emailInput = document.createElement("input");
        emailInput.type = "email";
        emailInput.value = editExternalEmail;
        if (!emailInput.checkValidity()) {
            toast.warning("Please enter a valid email address.");
            return;
        }
        const updated = [...editGroupData.external_members, { email: editExternalEmail, name: editExternalName || "Guest" }];
        setEditGroupData({ ...editGroupData, external_members: updated });
        setEditExternalEmail("");
        setEditExternalName("");
    };

    const handleEditRemoveExternal = (index) => {
        const updated = [...editGroupData.external_members];
        updated.splice(index, 1);
        setEditGroupData({ ...editGroupData, external_members: updated });
    };

    const handleEditRemoveDirectoryMember = (index) => {
        const updated = [...editGroupData.directory_members];
        updated.splice(index, 1);
        setEditGroupData({ ...editGroupData, directory_members: updated });
    };

    const renderGroupCard = (group) => {
        const showMembers = showMembersMap[group._id];
        const isEditing = editingGroupId === group._id;

        if (isEditing) {
            return (
                <div key={group._id} className="card mb-3">
                    <div className="card-body">
                        <h5>Edit Email List</h5>
                        <input 
                            type="text" 
                            className="form-control mb-2" 
                            placeholder="Group Label (Shown to members)" 
                            value={editGroupData.group_name} 
                            onChange={(e) => setEditGroupData({ ...editGroupData, group_name: e.target.value })} 
                        />
                        <textarea 
                            className="form-control mb-3" 
                            placeholder="Description (Optional, shown to members)" 
                            value={editGroupData.description} 
                            onChange={(e) => setEditGroupData({ ...editGroupData, description: e.target.value })} 
                        />
                        <input 
                            type="email" 
                            className="form-control mb-3" 
                            placeholder="Reply-To Email (Optional - where replies to emails will be sent)" 
                            value={editGroupData.reply_to_email} 
                            onChange={(e) => setEditGroupData({ ...editGroupData, reply_to_email: e.target.value })} 
                        />
                        
                        {/* <h6>Automatically add by role</h6>
                        <Select isMulti options={statusOptions} value={editSelectedStatuses} onChange={setEditSelectedStatuses} className="mb-3" />

                        <h6>Automatically add by committee</h6>
                        <Select isMulti options={committeeOptions} value={editSelectedCommittees} onChange={setEditSelectedCommittees} className="mb-3" />

                        <h6>Automatically add by executive role</h6>
                        <Select isMulti options={boardRoleOptions} value={editSelectedBoardRoles} onChange={setEditSelectedBoardRoles} className="mb-3" />

                        <h6>Add specific directory members</h6>
                        {editGroupData.directory_members.map((memberId, index) => (
                            <div key={index} className="d-flex mb-2 gap-2 align-items-center">
                                <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                                    <Select
                                        options={directory.map(user => ({
                                            value: user._id,
                                            label: `${user.surname}, ${user.first_name}${user.known_as ? ` (${user.known_as})` : ""}`
                                        }))}
                                        value={directory.find(u => u._id === memberId) ? {
                                            value: memberId,
                                            label: (() => {
                                                const user = directory.find(u => u._id === memberId);
                                                return `${user.surname}, ${user.first_name}${user.known_as ? ` (${user.known_as})` : ""}`;
                                            })()
                                        } : null}
                                        onChange={(selected) => {
                                            const updated = [...editGroupData.directory_members];
                                            updated[index] = selected ? selected.value : "";
                                            setEditGroupData({ ...editGroupData, directory_members: updated });
                                        }}
                                        isClearable
                                        placeholder="Select member..."
                                    />
                                </div>
                                <button className="btn btn-outline-danger btn-sm" onClick={() => handleEditRemoveDirectoryMember(index)}>×</button>
                            </div>
                        ))}
                        <button className="btn btn-custom-outline btn-sm mb-3" onClick={() => setEditGroupData(prev => ({ ...prev, directory_members: [...prev.directory_members, ""] }))}>
                            + Add Directory Member
                        </button> */}

                        <h6>External Members</h6>
                        {editGroupData.external_members.map((member, index) => (
                            <div key={index} className="d-flex mb-2 gap-2 align-items-center">
                                <span className="flex-grow-1">{member.name} ({member.email})</span>
                                <button className="btn btn-outline-danger btn-sm" onClick={() => handleEditRemoveExternal(index)}>×</button>
                            </div>
                        ))}
                        <div className="d-flex gap-2 mb-3">
                            <input 
                                type="email" 
                                className="form-control" 
                                placeholder="Email" 
                                value={editExternalEmail} 
                                onChange={(e) => setEditExternalEmail(e.target.value)} 
                            />
                            <input 
                                type="text" 
                                className="form-control" 
                                placeholder="Name (optional)" 
                                value={editExternalName} 
                                onChange={(e) => setEditExternalName(e.target.value)} 
                            />
                            <button className="btn btn-custom-outline" onClick={handleEditAddExternal}>Add</button>
                        </div>

                        <div className="d-flex gap-2">
                            <button className="btn btn-admin" onClick={handleEditSave}>Save Changes</button>
                            <button className="btn btn-outline-secondary" onClick={handleEditCancel}>Cancel</button>
                        </div>
                    </div>
                </div>
            );
        }

        return (
            <div key={group._id} className="card mb-3">
                <div className="card-body">
                    <div className="d-flex justify-content-between">
                        <div>
                            <h5>
                                {group.group_name} 
                                <span className="text-muted"> ({group.members.length} members)</span>
                                {group.is_publicly_joinable && (
                                    <span className="badge bg-success ms-2">
                                        <i className="bi bi-globe"></i> Public
                                    </span>
                                )}
                            </h5>
                        </div>
                        <div className="d-flex gap-2">
                            <button className="btn btn-custom-outline btn-sm" onClick={() => handleEditGroup(group)}>Edit</button>
                            <button className="btn btn-outline-secondary btn-sm" onClick={() => handleArchive(group._id)}>Delete</button>
                        </div>
                    </div>
                    <p>{group.description}</p>
                    {group.reply_to_email && (
                        <p className="text-muted">
                            <strong>Reply-To:</strong> {group.reply_to_email}
                        </p>
                    )}
                    <button className="btn btn-sm btn-custom-outline mb-2" onClick={() => toggleShowMembers(group._id)}>
                        {showMembers ? "Hide Members" : "Show Members"}
                    </button>
                    {showMembers && (
                        <ul className="list-group mb-3">
                            {group.members.map((m, idx) => (
                                <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                    <div>
                                        {m.name} ({m.email})
                                        {m.status && <span className="badge bg-primary ms-2">{m.status}</span>}
                                        {!m.user_id && <span className="badge bg-secondary ms-2">External</span>}
                                    </div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        );
    };

    const handleShowCreateForm = () => {
        if (showCreate) {
            handleCancel();
        } else {
            const lastReplyTo = getLastReplyToEmail();
            setNewGroupData(prev => ({ 
                ...prev, 
                reply_to_email: lastReplyTo 
            }));
            setShowCreate(true);
        }
    };

    return (
        <div className="container mt-2">
            <button className="btn btn-admin mb-3" onClick={handleShowCreateForm}>
                {showCreate ? "Cancel" : "Create New Email List"}
            </button>

            {showCreate && (
                <div className="card p-3 mb-4">
                    <h5>Create Email List</h5>
                    <input type="text" className="form-control mb-2" placeholder="Group Label (Shown to members)" value={newGroupData.group_name} onChange={(e) => setNewGroupData({ ...newGroupData, group_name: e.target.value })} />
                    <textarea className="form-control mb-3" placeholder="Description (Optional, shown to members)" value={newGroupData.description} onChange={(e) => setNewGroupData({ ...newGroupData, description: e.target.value })} />
                    <input 
                        type="email" 
                        className="form-control mb-3" 
                        placeholder="Reply-To Email (Optional - where replies to emails will be sent)" 
                        value={newGroupData.reply_to_email} 
                        onChange={(e) => setNewGroupData({ ...newGroupData, reply_to_email: e.target.value })} 
                    />
                    
                    {/* <h6>Quick add by role</h6>
                    <Select isMulti options={statusOptions} value={selectedStatuses} onChange={setSelectedStatuses} className="mb-3" />

                    <h6>Quick add by committee</h6>
                    <Select isMulti options={committeeOptions} value={selectedCommittees} onChange={setSelectedCommittees} className="mb-3" />

                    <h6>Quick add by executive role</h6>
                    <Select isMulti options={boardRoleOptions} value={selectedBoardRoles} onChange={setSelectedBoardRoles} className="mb-3" /> */}

                    {/* <h6>Add specific directory members</h6>
                    {newGroupData.directory_members.map((memberId, index) => (
                        <div key={index} className="d-flex mb-2 gap-2 align-items-center">
                            <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                                <Select
                                    options={directory.map(user => ({
                                        value: user._id,
                                        label: `${user.surname}, ${user.first_name}${user.known_as ? ` (${user.known_as})` : ""}`
                                    }))}
                                    value={directory.map(user => ({
                                        value: user._id,
                                        label: `${user.surname}, ${user.first_name}${user.known_as ? ` (${user.known_as})` : ""}`
                                    })).find(option => option.value === memberId)}
                                    onChange={(selectedOption) => handleDirectorySelect(index, selectedOption)}
                                    isClearable
                                    placeholder="Select member"
                                    styles={{ container: (base) => ({ ...base, width: "100%" }) }}
                                />
                            </div>
                            <button className="btn btn-outline-danger btn-sm" onClick={() => handleRemoveDirectoryMember(index)}>
                                ×
                            </button>
                        </div>
                    ))}
                    <button className="btn btn-custom-outline btn-sm mb-3" onClick={handleAddDirectoryMember}>+ Add Member</button> */}

                    <h6>Add external emails</h6>
                    <div className="row mb-2">
                        <div className="col">
                            <input type="email" className="form-control" placeholder="Email" value={externalEmail} onChange={(e) => setExternalEmail(e.target.value)} />
                        </div>
                        <div className="col">
                            <input type="text" className="form-control" placeholder="Name (optional)" value={externalName} onChange={(e) => setExternalName(e.target.value)} />
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-custom-outline" onClick={handleAddExternal}>Add</button>
                        </div>
                    </div>

                    <div className="mb-2">
                        {newGroupData.external_members.map((m, idx) => (
                            <span key={idx} className="badge bg-secondary me-1">
                                {m.name} ({m.email})
                            </span>
                        ))}
                    </div>

                    <button className="btn btn-admin mt-2" onClick={handleCreate}>Save List</button>
                </div>
            )}

            {groups.map(renderGroupCard)}
        </div>
    );
}
